import React from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { ArrowRight, CheckCircle2, X } from 'lucide-react'
import type { MappingResult } from '@/lib/combined-inventory'

interface MappingSavedNoticeProps {
  result: MappingResult
  onDismiss: () => void
}

// WHAT A ROOM MAPPING SAVE DID ("Optimistic room mapping"): one sentence, and, only when something
// could not be fully settled, "1 thing to check" linking to where it is fixed. The step never refuses
// what the system can handle, so this is where those things surface: fix later, not first.
//
// Every word and the link are the server's (App::RoomMappingOutcome). Not a toast, because the link
// has to be there to follow; it stays until dismissed or the page is left.
export default function MappingSavedNotice({ result, onDismiss }: MappingSavedNoticeProps) {
  return (
    <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
      <div className="flex flex-wrap items-start gap-3 rounded-xl border border-emerald-200 bg-white px-5 py-4 shadow-sm">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
        <div role="status" className="min-w-0 flex-1 space-y-1">
          <p className="text-sm font-medium text-foreground">{result.headline}</p>
          {result.checksLabel && result.checksHref && (
            <Link
              href={result.checksHref}
              className="inline-flex cursor-pointer items-center gap-1 rounded text-xs font-semibold text-amber-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              {result.checksLabel}
              <ArrowRight className="h-3 w-3" aria-hidden="true" />
            </Link>
          )}
          {/* Offsets are never set on the mapping: where they are, when a mapped listing has a Same room. */}
          {result.offsetsLabel && result.offsetsHref && (
            <p>
              <Link
                href={result.offsetsHref}
                className="inline-flex cursor-pointer items-center gap-1 rounded text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {result.offsetsLabel}
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="-mr-1.5 -mt-1 h-8 w-8 shrink-0 rounded-lg p-0 text-muted-foreground hover:text-foreground"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </section>
  )
}
